import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Toast } from '../ui';
import Drawer from './Drawer';
import FieldWidget from './FieldWidget';
import ImageGenModal from './ImageGenModal';
import ProductCategoriesPanel from './ProductCategoriesPanel';
import ProductTagsPanel from './ProductTagsPanel';
import WorkflowPanel from './WorkflowPanel';
import {
  getProduct,
  saveProductValues,
  setProductPublication,
  getProductHistory,
  previewEnrich,
  commitEnrich,
  createEnrichJob,
  subscribeToEnrichJobStream,
  assetUrl,
} from '../../services/pimApi';
import './pim.css';
import './pim-oc.css';

const GLOBAL = 0;

/* Product editor — Operations Console redesign.
   The state model and API flows are unchanged from the previous editor
   (scoped EAV edits keyed code|channel|locale, publish switch gated on
   completeness, enrich preview + job SSE, ImageGenModal, history drawer).
   Gap data — summary bar, tab badges, field gap notes — reads the stored
   publications' missing_required, the same source as the list chips and
   the publish gate. */

/* Compute the (channel_id, locale_id) a value is stored under for an attribute
 * given the active region/locale context and the attribute's scope flags. */
function scopeFor(attr, channelId, localeId) {
  return {
    channel_id: attr.is_scopable ? channelId : GLOBAL,
    locale_id: attr.is_localizable ? localeId : GLOBAL,
  };
}

function keyOf(code, channelId, localeId) {
  return `${code}|${channelId}|${localeId}`;
}

function StatusLine({ progress }) {
  const c = progress?.counters || {};
  const parts = [progress?.status || 'running'];
  if (c.current_sku) parts.push(`SKU ${c.current_sku}`);
  if (typeof c.processed === 'number') {
    parts.push(`${c.processed}/${c.eligible ?? '?'} processed`);
  }
  if (typeof c.enriched === 'number') parts.push(`${c.enriched} enriched`);
  return parts.join(' · ');
}

const ENV_LABELS = { prod: 'production', production: 'production', staging: 'staging' };
const isProdEnv = (e) => e === 'prod' || e === 'production';

/* Per-environment BigCommerce ids for one market. `akeneo_id` (from
 * product_channel_locales) is the prod id sourced from Akeneo, while
 * `mappings` (bigcommerce_product_mappings) may also carry a prod row — the
 * two are reconciled into a single Prod entry so it never shows twice. */
function bcEnvironmentRows(bc) {
  const mappings = bc?.mappings || [];
  const akeneoId = bc?.akeneo_id ?? null;
  const prodMapping = mappings.find((m) => isProdEnv(m.environment));
  const prodDisagrees =
    akeneoId != null && prodMapping && prodMapping.bc_product_id !== akeneoId;
  const rows = [
    {
      environment: 'prod',
      id: prodDisagrees ? prodMapping.bc_product_id : akeneoId ?? prodMapping?.bc_product_id ?? null,
      hint: prodDisagrees ? `differs from Akeneo #${akeneoId}` : null,
    },
  ];
  const rest = mappings.filter((m) => !isProdEnv(m.environment));
  const staging = rest.find((m) => m.environment === 'staging');
  rows.push({ environment: 'staging', id: staging?.bc_product_id ?? null, hint: null });
  rest
    .filter((m) => m.environment !== 'staging')
    .forEach((m) => rows.push({ environment: m.environment, id: m.bc_product_id, hint: null }));
  return rows;
}

const ATTR_LABELS = {
  product_name: 'name',
  name: 'name',
  description: 'description',
  primary_image: 'primary image',
  list_price: 'price',
};
const gapLabel = (code) => ATTR_LABELS[code] || code.replace(/_/g, ' ');

// The enrich job writes name + description; the image modal fixes
// primary_image. Other gaps get a note but no fix button.
const GAP_NOTES = {
  product_name: 'The SKU is showing in the store instead of a name.',
  name: 'The SKU is showing in the store instead of a name.',
  description: 'Nothing written yet — nothing for shoppers to read.',
  primary_image: 'No primary image. Generate one, or upload your own.',
  list_price: 'Waiting on the Epicor price sync. Not editable here.',
};
const ENRICH_FIXABLE = new Set(['product_name', 'name', 'description']);

function scopeChip(attr) {
  if (attr.is_scopable && attr.is_localizable) return 'Per-region · localisable';
  if (attr.is_scopable) return 'Per-region';
  if (attr.is_localizable) return 'Localisable';
  return 'Global';
}

export default function ProductEditor() {
  const { sku } = useParams();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChannel, setActiveChannel] = useState(GLOBAL);
  const [activeLocale, setActiveLocale] = useState(GLOBAL);
  const [activeGroup, setActiveGroup] = useState(null);
  const [edits, setEdits] = useState({}); // key -> value
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState(null);
  const [imageGenOpen, setImageGenOpen] = useState(false);
  const [dataRevision, setDataRevision] = useState(0);

  const applyProductDetail = useCallback((data) => {
    setDetail(data);
    setEdits({});
    setDataRevision((n) => n + 1);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProduct(sku, { _t: Date.now() });
      applyProductDetail(data);
      const firstChannel = data.channels[0];
      if (firstChannel) {
        setActiveChannel(firstChannel.id);
        setActiveLocale(firstChannel.default_locale_id || GLOBAL);
      }
    } catch (e) {
      setToast({ message: 'Failed to load product', error: true });
    } finally {
      setLoading(false);
    }
  }, [sku, applyProductDetail]);

  /** Re-fetch after save/enrich without blanking the whole page. */
  const refreshProduct = useCallback(async () => {
    try {
      const data = await getProduct(sku, { _t: Date.now() });
      applyProductDetail(data);
    } catch (e) {
      setToast({ message: 'Failed to reload product', error: true });
    }
  }, [sku, applyProductDetail]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Index stored values by attribute_code|channel|locale.
  const valueIndex = useMemo(() => {
    const idx = {};
    (detail?.values || []).forEach((v) => {
      idx[keyOf(v.attribute_code, v.channel_id, v.locale_id)] = v.value;
    });
    return idx;
  }, [detail]);

  const groups = useMemo(() => {
    const map = new Map();
    (detail?.attributes || []).forEach((a) => {
      const gc = a.group_code || '_ungrouped';
      if (!map.has(gc)) map.set(gc, { code: gc, label: a.group_label || 'General', attrs: [] });
      map.get(gc).attrs.push(a);
    });
    return Array.from(map.values());
  }, [detail]);

  useEffect(() => {
    if (groups.length && !activeGroup) setActiveGroup(groups[0].code);
  }, [groups, activeGroup]);

  const currentValue = (attr) => {
    // Unsaved edits are keyed at the attribute's precise scope (see setValue).
    const { channel_id, locale_id } = scopeFor(attr, activeChannel, activeLocale);
    const editKey = keyOf(attr.code, channel_id, locale_id);
    if (editKey in edits) return edits[editKey];
    // Display fallback mirrors backend resolve_display_value: walk the raw
    // (channel, locale) context through (c,l) -> (c,0) -> (0,l) -> (0,0) so a
    // globally-backfilled value (e.g. name/brand written at (0,0)) still shows
    // when a region/locale is selected. Edits stay scoped, so save is unchanged.
    for (const [c, l] of [
      [activeChannel, activeLocale],
      [activeChannel, GLOBAL],
      [GLOBAL, activeLocale],
      [GLOBAL, GLOBAL],
    ]) {
      const k = keyOf(attr.code, c, l);
      if (k in valueIndex) return valueIndex[k];
    }
    return null;
  };

  const setValue = (attr, val) => {
    const { channel_id, locale_id } = scopeFor(attr, activeChannel, activeLocale);
    setEdits((prev) => ({ ...prev, [keyOf(attr.code, channel_id, locale_id)]: val }));
  };

  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      const values = Object.entries(edits).map(([k, value]) => {
        const [attribute_code, channel_id, locale_id] = k.split('|');
        return { attribute_code, value, channel_id: Number(channel_id), locale_id: Number(locale_id) };
      });
      await saveProductValues(sku, values);
      setToast({ message: `Saved ${values.length} value(s)` });
      await reload();
    } catch (e) {
      const detailErr = e?.response?.data?.detail;
      if (detailErr && detailErr.errors) {
        const msg = detailErr.errors.map((x) => `${x.attribute_code}: ${x.error}`).join(' · ');
        setToast({ message: `Validation failed — ${msg}`, error: true });
      } else {
        setToast({ message: 'Save failed', error: true });
      }
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (channelId, next) => {
    try {
      await setProductPublication(sku, channelId, { published: next });
      await reload();
      setToast({ message: next ? 'Published' : 'Unpublished' });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && d.message) {
        setToast({ message: `${d.message} (${d.completeness}%)`, error: true });
      } else {
        setToast({ message: 'Publish change failed', error: true });
      }
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      setHistory(await getProductHistory(sku));
    } catch (e) {
      setHistory([]);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    try {
      setPreviewData(await previewEnrich(sku));
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Preview failed', error: true });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runEnrich = async ({ skipConfirm = false, force = false } = {}) => {
    if (
      !skipConfirm &&
      !window.confirm(
        'Run AI enrichment for this SKU? This uses SerpAPI + Gemini (API cost applies) and overwrites product_name and description.'
      )
    ) {
      return;
    }
    setEnriching(true);
    setEnrichProgress({ status: 'pending' });
    try {
      const job = await createEnrichJob({ skus: [sku], force });
      subscribeToEnrichJobStream(
        job.id,
        (payload) => {
          setEnrichProgress(payload);
          if (payload?.status === 'succeeded') {
            setEnriching(false);
            refreshProduct();
            setToast({ message: 'Enrichment complete — product updated' });
          }
          if (payload?.status === 'failed' || payload?.status === 'cancelled') {
            setEnriching(false);
            setToast({
              message: payload?.error_detail || `Enrichment ${payload.status}`,
              error: payload?.status !== 'cancelled',
            });
          }
        },
        () => setEnriching(false)
      );
    } catch (e) {
      setEnriching(false);
      setToast({ message: e?.response?.data?.detail || 'Failed to start enrichment', error: true });
    }
  };

  const commitPreview = async () => {
    if (!previewData?.product_name || !previewData?.description) {
      setToast({ message: 'Nothing to commit — run preview first', error: true });
      return;
    }
    setCommitting(true);
    try {
      await commitEnrich({
        sku,
        product_name: previewData.product_name,
        description: previewData.description,
      });
      setPreviewOpen(false);
      setPreviewData(null);
      await refreshProduct();
      setToast({ message: 'Enrichment saved to PIM' });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Failed to save enrichment', error: true });
    } finally {
      setCommitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pim-oc">
        <Link to="/pim/products" className="oc-back">← All products</Link>
        <div className="oc-frame oc-blueprint" style={{ padding: 'var(--oc-space-6)' }}>
          Loading…
        </div>
      </div>
    );
  }
  if (!detail) return null;

  const { product, channels, locales, publications } = detail;
  // Current primary image (any scope) for the rail and the AI review's
  // "will be replaced" pane.
  const primaryImageAssetId = (detail.values || []).find(
    (v) => v.attribute_code === 'primary_image' && v.value?.asset_id
  )?.value?.asset_id;
  const pubByChannel = Object.fromEntries(publications.map((p) => [p.channel_id, p]));
  const activePub = pubByChannel[activeChannel];
  const missingForChannel = activePub?.missing_required || [];
  const activePct = activePub?.completeness ?? 0;
  const requiredAttrs = (detail.attributes || []).filter((a) => a.is_required);
  const filledCount = Math.max(0, requiredAttrs.length - missingForChannel.length);

  const bcByMarket = Object.fromEntries((detail.bigcommerce_ids || []).map((b) => [b.market_code, b]));
  // Every market with a channel gets a BigCommerce block, even before any push.
  const marketCodes = Array.from(
    new Set([...channels.map((c) => c.market_code), ...Object.keys(bcByMarket)]),
  );
  const group = groups.find((g) => g.code === activeGroup);
  const groupForAttr = (code) => groups.find((g) => g.attrs.some((a) => a.code === code));
  const gapCountFor = (g) => g.attrs.filter((a) => missingForChannel.includes(a.code)).length;

  const nameAttr = { code: 'product_name', is_scopable: true, is_localizable: true };
  const rawName = currentValue(nameAttr) ?? currentValue({ ...nameAttr, code: 'name' });
  const displayName = typeof rawName === 'string' && rawName.trim() ? rawName : null;

  const autoMissing = missingForChannel.filter((c) => ENRICH_FIXABLE.has(c)).length;
  const enrichLabel = autoMissing > 0 ? `Enrich ${autoMissing} field${autoMissing === 1 ? '' : 's'}` : 'Enrich';

  const openMediaTab = () => {
    const g = groupForAttr('primary_image');
    if (g) setActiveGroup(g.code);
    else setImageGenOpen(true);
  };

  return (
    <div className="pim-oc">
      <Link to="/pim/products" className="oc-back">← All products</Link>

      <div className="oc-header" style={{ alignItems: 'flex-start', marginBottom: 'var(--oc-space-4)' }}>
        <div className="oc-header-text">
          <div className="oc-kicker">
            <span className="oc-kicker-sku">{product.identifier}</span>
            <span className="oc-kicker-sep" />
            <span className="oc-kicker-fam">{product.family_label || 'Unassigned family'}</span>
            {product.enriched && (
              <>
                <span className="oc-kicker-sep" />
                <span className="oc-kicker-fam">
                  Enriched{product.enrich_date ? ` · ${new Date(product.enrich_date).toLocaleDateString()}` : ''}
                </span>
              </>
            )}
          </div>
          <h1 className={'oc-title oc-prodname' + (displayName ? '' : ' missing')}>
            {displayName || 'No name yet'}
          </h1>
        </div>
        <div className="oc-header-action" style={{ display: 'flex', gap: 'var(--oc-space-2)', flexWrap: 'wrap' }}>
          <button type="button" className="oc-btn oc-btn-secondary" onClick={runPreview} disabled={previewLoading || enriching}>
            {previewLoading ? 'Preview…' : 'Preview enrich'}
          </button>
          <button type="button" className="oc-btn oc-btn-secondary" onClick={runEnrich} disabled={enriching || previewLoading}>
            {enriching ? 'Enriching…' : enrichLabel}
          </button>
          <button type="button" className="oc-btn oc-btn-secondary" onClick={() => setImageGenOpen(true)} disabled={imageGenOpen}>
            Generate image
          </button>
          <button type="button" className="oc-btn oc-btn-secondary" onClick={openHistory}>
            History
          </button>
          <button type="button" className="oc-btn oc-btn-primary" onClick={save} disabled={!dirtyCount || saving}>
            {saving ? 'Saving…' : dirtyCount ? `Save (${dirtyCount})` : 'Saved'}
          </button>
        </div>
      </div>

      <WorkflowPanel sku={sku} />

      {enriching && enrichProgress && (
        <div className="oc-review-banner">
          <span className="oc-review-headline">Enrichment job</span>
          <span className="oc-review-copy"><StatusLine progress={enrichProgress} /></span>
        </div>
      )}

      <div className="oc-summary oc-blueprint">
        <i className="oc-corner tl" /><i className="oc-corner tr" />
        <i className="oc-corner bl" /><i className="oc-corner br" />
        <span className={'oc-summary-text' + (activePct === 100 ? ' full' : '')}>
          {filledCount} of {requiredAttrs.length} required fields complete
        </span>
        <div className="oc-summary-bar" title={`${activePct}% complete`}>
          <div
            className={`oc-summary-fill oc-pbar-fill ${activePct === 100 ? 'full' : activePct >= 67 ? 'mid' : 'low'}`}
            style={{ width: `${activePct}%` }}
          />
        </div>
        {activePct === 100 && <span className="oc-readychip">Ready to publish</span>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {missingForChannel.map((code) => (
            <button
              key={code}
              type="button"
              className="oc-gapjump"
              onClick={() => {
                const g = groupForAttr(code);
                if (g) setActiveGroup(g.code);
              }}
            >
              Missing {gapLabel(code)}
            </button>
          ))}
        </div>
      </div>

      <div className="oc-edgrid">
        <div className="oc-rail">
          <div className="oc-rail-card oc-blueprint">
            <i className="oc-corner tl" /><i className="oc-corner tr" />
            <i className="oc-corner bl" /><i className="oc-corner br" />
            <h4 className="oc-rail-title">Main image</h4>
            {primaryImageAssetId ? (
              <div>
                <div className="oc-imgbox">
                  <img src={assetUrl(primaryImageAssetId)} alt={displayName || product.identifier} />
                </div>
                <div className="oc-imgmeta">
                  <span className="oc-imgmeta-text">Primary image</span>
                  <button type="button" className="oc-openbtn" onClick={openMediaTab}>Replace</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="oc-imgbox-empty">No image yet</div>
                <button
                  type="button"
                  className="oc-btn oc-btn-secondary"
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={() => setImageGenOpen(true)}
                >
                  Generate image
                </button>
              </div>
            )}
          </div>

          <div className="oc-rail-card oc-blueprint">
            <i className="oc-corner tl" /><i className="oc-corner tr" />
            <i className="oc-corner bl" /><i className="oc-corner br" />
            <h4 className="oc-rail-title">Storefronts</h4>
            {channels.map((c) => {
              const pub = pubByChannel[c.id];
              const pct = pub?.completeness ?? 0;
              const live = !!pub?.published;
              return (
                <div
                  key={c.id}
                  className={'oc-storerow' + (activeChannel === c.id ? ' active' : '')}
                  onClick={() => { setActiveChannel(c.id); setActiveLocale(c.default_locale_id || GLOBAL); }}
                  title="Edit this region"
                >
                  <span className="oc-storemkt">{c.market_code}</span>
                  <span className="oc-storenote"><code>{c.code}</code></span>
                  <span className={`oc-regchip ${live ? 'live' : 'draft'}`}>{live ? 'Live' : 'Draft'}</span>
                  <label className="pim-switch" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={live}
                      disabled={!live && pct < 100}
                      onChange={(e) => togglePublish(c.id, e.target.checked)}
                    />
                    <span />
                  </label>
                </div>
              );
            })}
            {channels.length === 0 && <p className="oc-fhelp">No regions configured.</p>}
            <div className="oc-field" style={{ marginTop: 'var(--oc-space-3)' }}>
              <label htmlFor="pd-loc">Editing locale</label>
              <select
                id="pd-loc"
                className="oc-input"
                value={activeLocale}
                onChange={(e) => setActiveLocale(Number(e.target.value))}
              >
                <option value={GLOBAL}>Global (unscoped)</option>
                {locales.map((l) => (
                  <option key={l.id} value={l.id}>{l.label} ({l.code})</option>
                ))}
              </select>
            </div>
            <p className="oc-fhelp" style={{ marginTop: 8 }}>
              Fields marked per-region write to this locale only. Global fields are shared by
              both storefronts.
            </p>
          </div>

          <div className="oc-rail-card oc-blueprint">
            <i className="oc-corner tl" /><i className="oc-corner tr" />
            <i className="oc-corner bl" /><i className="oc-corner br" />
            <h4 className="oc-rail-title">Store records</h4>
            {marketCodes.map((mc) =>
              bcEnvironmentRows(bcByMarket[mc]).map((row) => (
                <div key={`${mc}-${row.environment}`} className="oc-kvrow">
                  <span className="oc-kvrow-k">{mc} · {ENV_LABELS[row.environment] || row.environment}</span>
                  {row.id != null ? (
                    <span className="oc-kvrow-v" title={row.hint || undefined}>
                      #{row.id}
                      {row.hint && (
                        <span className="oc-kvrow-k" style={{ marginLeft: 6, fontSize: 11 }}>({row.hint})</span>
                      )}
                    </span>
                  ) : (
                    <span className="oc-kvrow-v dim">Not pushed</span>
                  )}
                </div>
              ))
            )}
            {marketCodes.length === 0 && <p className="oc-fhelp">No markets configured.</p>}
          </div>

          <ProductCategoriesPanel sku={sku} />

          <ProductTagsPanel sku={sku} />
        </div>

        <div className="oc-frame oc-blueprint" style={{ marginBottom: 0 }}>
          <i className="oc-corner tl" /><i className="oc-corner tr" />
          <i className="oc-corner bl" /><i className="oc-corner br" />
          <div className="oc-edtabs" role="tablist" aria-label="Attribute groups">
            {groups.map((g) => {
              const gaps = gapCountFor(g);
              return (
                <button
                  key={g.code}
                  type="button"
                  role="tab"
                  aria-selected={g.code === activeGroup}
                  className={'oc-edtab' + (g.code === activeGroup ? ' on' : '')}
                  onClick={() => setActiveGroup(g.code)}
                >
                  {g.label}
                  {gaps > 0 && <span className="oc-gapbadge">{gaps}</span>}
                </button>
              );
            })}
          </div>

          <div className="oc-fields">
            {group?.attrs.map((attr) => {
              const sc = scopeFor(attr, activeChannel, activeLocale);
              const helpText = attr.settings?.help || null;
              const hasGap = missingForChannel.includes(attr.code);
              const fixAction = hasGap && ENRICH_FIXABLE.has(attr.code)
                ? { label: 'Enrich this field', run: () => runEnrich() }
                : hasGap && attr.code === 'primary_image'
                  ? { label: 'Generate', run: () => setImageGenOpen(true) }
                  : null;
              return (
                <div key={attr.id} className="oc-frow">
                  <div style={{ minWidth: 0 }}>
                    <div className="oc-flabel">
                      {attr.label}
                      {attr.is_required && <span className="oc-fstar"> *</span>}
                    </div>
                    <div className="oc-fmeta">
                      <span className="oc-fcode">{attr.code}</span>
                      <span className="oc-fscope">{scopeChip(attr)}</span>
                    </div>
                    {helpText && <div className="oc-fhelp">{helpText}</div>}
                  </div>
                  <div className="oc-fcontrol">
                    <FieldWidget
                      key={`${attr.code}-${sc.channel_id}-${sc.locale_id}-${dataRevision}`}
                      sku={sku}
                      attribute={attr}
                      value={currentValue(attr)}
                      scope={sc}
                      onChange={(v) => setValue(attr, v)}
                    />
                    {hasGap && (
                      <div className="oc-fgap">
                        <span className="oc-fgap-note">
                          {GAP_NOTES[attr.code] || 'Required for this channel.'}
                        </span>
                        {fixAction && (
                          <button type="button" className="oc-fixbtn" onClick={fixAction.run}>
                            {fixAction.label}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {(!group || group.attrs.length === 0) && (
              <p className="oc-fhelp" style={{ padding: 'var(--oc-space-4) 0' }}>
                This product has no family attributes. Assign a family with attributes to enrich it.
              </p>
            )}
          </div>
        </div>
      </div>

      {previewOpen && (
        <Drawer
          title="Enrichment preview"
          sub={`${product.identifier} — SerpAPI + Gemini (not saved)`}
          onClose={() => setPreviewOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPreviewOpen(false)}>Close</Button>
              <Button onClick={commitPreview} disabled={committing || previewLoading || !previewData}>
                {committing ? 'Saving…' : 'Commit enrich'}
              </Button>
            </>
          }
        >
          {previewLoading && <p className="pim-muted">Generating preview…</p>}
          {previewData && (
            <>
              <p className="pim-muted">Name source: {previewData.source}</p>
              <div className="pim-field">
                <label>Product name</label>
                <p>{previewData.product_name}</p>
              </div>
              <div className="pim-field">
                <label>Description</label>
                <div dangerouslySetInnerHTML={{ __html: previewData.description }} />
              </div>
            </>
          )}
        </Drawer>
      )}

      {imageGenOpen && (
        <ImageGenModal
          sku={sku}
          currentImageUrl={primaryImageAssetId ? assetUrl(primaryImageAssetId) : null}
          onClose={() => setImageGenOpen(false)}
          onCommitted={(result) => {
            if (result?.committed) {
              refreshProduct();
              setToast({
                message:
                  result.destination === 'gallery'
                    ? 'AI image approved and added to gallery'
                    : 'AI image approved and set as primary image',
              });
            } else if (result?.rejected) {
              setToast({ message: 'AI image candidate rejected' });
            }
          }}
        />
      )}

      {historyOpen && (
        <Drawer title="Value history" sub={product.identifier} onClose={() => setHistoryOpen(false)}>
          {history.length === 0 ? (
            <p className="pim-muted">No changes recorded.</p>
          ) : (
            <ul className="pim-history">
              {history.map((h) => (
                <li key={h.id}>
                  <div className="pim-history-head">
                    <code>{h.attribute_code}</code>
                    <span className="pim-muted">{new Date(h.changed_at).toLocaleString()}</span>
                  </div>
                  <div className="pim-history-diff">
                    <span className="old">{JSON.stringify(h.old_value)}</span>
                    <span className="arrow">→</span>
                    <span className="new">{JSON.stringify(h.new_value)}</span>
                  </div>
                  <div className="pim-muted">by {h.changed_by}</div>
                </li>
              ))}
            </ul>
          )}
        </Drawer>
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </div>
  );
}
